"use client";

import { useState, type ReactNode } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ROLES_TREE, LOCATIONS_TREE, findLabelById } from "@/lib/taxonomy";
import { SEASON_TYPES } from "@/lib/seasons";
import { sendFirstMessageToEmployer } from "@/app/actions/messaging";
import type { UserRole } from "@/lib/auth";
import { VerifiedBadge } from "@/app/components/VerifiedBadge";
import { buildWorkSignupHref } from "@/lib/signup-next";

// ═══ TYPES ═══

interface Company {
  id: string;
  name: string;
  slug: string | null;
  logo_url: string | null;
  description: string | null;
  website: string | null;
  location: string | null;
  company_size: string | null;
  hiring_regions: string[] | null;
  hiring_roles: string[] | null;
  hiring_season_types: string[] | null;
  allow_inbound_messages?: boolean;
  actively_hiring?: boolean;
  brand_color: string | null;
  perks: string[] | null;
  created_at: string;
  verified: boolean;
  avg_response_hours: number | null;
}

interface CandidateProfile {
  id: string;
  availabilityWindows: {
    id: string;
    season_label: string;
    status: string;
  }[];
}

const SEASON_TYPE_COLORS: Record<string, string> = {
  winter: "#1B2A4A",
  summer: "#0EA5E9",
  festivals: "#E8835C",
};

const COMPANY_SIZES: Record<string, string> = {
  "1-5": "1–5 employees",
  "6-20": "6–20 employees",
  "21-50": "21–50 employees",
  "51-100": "51–100 employees",
  "100+": "100+ employees",
};

// ═══ MAIN COMPONENT ═══

export function CompanyProfileContent({
  company,
  isSelf,
  userRole,
  candidateProfile,
  existingConversations,
  designatedJobsHref,
  openRoles = null,
}: {
  company: Company;
  isSelf: boolean;
  userRole?: UserRole;
  candidateProfile?: CandidateProfile | null;
  existingConversations?: { id: string; windowId: string | null }[];
  designatedJobsHref: string | null;
  /** Server-rendered open roles list, shown after the About section. */
  openRoles?: ReactNode;
}) {
  const router = useRouter();
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [selectedWindowId, setSelectedWindowId] = useState("");
  const [messageContent, setMessageContent] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [showAllLocations, setShowAllLocations] = useState(false);

  const canMessage =
    userRole === "candidate" &&
    company.allow_inbound_messages &&
    candidateProfile &&
    candidateProfile.availabilityWindows.length > 0;

  const conversations = existingConversations ?? [];
  const allWindowsMessaged =
    candidateProfile &&
    candidateProfile.availabilityWindows.length > 0 &&
    candidateProfile.availabilityWindows.every((w) =>
      conversations.some((c) => c.windowId === w.id)
    );

  const showCTA = canMessage && !allWindowsMessaged && !isSelf;

  async function handleSendMessage() {
    if (!selectedWindowId || !messageContent.trim()) return;
    setSending(true);
    setSendError(null);

    const result = await sendFirstMessageToEmployer(
      company.id,
      messageContent.trim(),
      selectedWindowId
    );

    if (result.error) {
      if (result.conversationId) {
        router.push(`/inbox/${result.conversationId}`);
      } else {
        setSendError(result.error);
      }
    } else {
      setSent(true);
    }
    setSending(false);
  }

  function handleCTAClick() {
    if (showCTA) {
      setShowMessageForm(true);
      setTimeout(() => {
        document.getElementById("message-section")?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else if (!userRole) {
      router.push(buildWorkSignupHref({ next: "/jobs", source: "company-profile" }));
    }
  }

  const firstSeasonColor = (company.hiring_season_types ?? [])
    .map((t) => SEASON_TYPE_COLORS[t])
    .find(Boolean) ?? "#1B2840";
  const accentColor = company.brand_color || firstSeasonColor;
  const sizeLabel = company.company_size
    ? COMPANY_SIZES[company.company_size] ?? company.company_size
    : null;
  const roleLabels = (company.hiring_roles ?? [])
    .map((id) => findLabelById(ROLES_TREE, id) ?? id)
    .filter(Boolean);
  const seasonLabels = (company.hiring_season_types ?? [])
    .map((id) => SEASON_TYPES.find((st) => st.id === id)?.label ?? id);
  const locationLabels = (company.hiring_regions ?? [])
    .map((id) => findLabelById(LOCATIONS_TREE, id) ?? id)
    .filter(Boolean);
  const maxLocations = 8;
  const hasMoreLocations = locationLabels.length > maxLocations;
  const visibleLocations = showAllLocations ? locationLabels : locationLabels.slice(0, maxLocations);
  const perks = company.perks ?? [];
  const initials = company.name
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen bg-warm-bg">
    <div className="max-w-[640px] mx-auto px-5 pt-6 pb-28 animate-fade-in">
      {/* ─── Self-View Banner ─── */}
      {isSelf && (
        <div className="bg-gold/10 border border-gold/30 rounded-[18px] px-5 py-4 mb-4 flex items-center justify-between gap-4">
          <p className="text-[14px] font-medium text-charcoal">
            This is how candidates see your company.
          </p>
          <Link
            href="/employer/company"
            className="text-[13px] font-semibold text-gold no-underline hover:text-gold-light transition-colors shrink-0"
          >
            Edit Profile &rarr;
          </Link>
        </div>
      )}

      {/* ─── Back Link ─── */}
      <button
        onClick={() => router.back()}
        className="text-[14px] text-text-muted bg-transparent border-none cursor-pointer p-0 hover:text-navy transition-colors inline-flex items-center gap-1.5 mb-4 font-sans"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10 12L6 8L10 4" />
        </svg>
        Back
      </button>

      {/* ─── Hero Card ─── */}
      <div className="bg-white rounded-[18px] border border-border-light shadow-sm overflow-hidden mb-4">
        {/* Colour banner */}
        <div
          className="h-[90px] relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}CC)` }}
        >
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(28, 138, 153,0.12),transparent_70%)]" aria-hidden="true" />
        </div>

        <div className="px-5 sm:px-6 pt-5 pb-6">
          {/* Avatar */}
          {company.logo_url ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={company.logo_url}
              alt={`${company.name} logo`}
              className="w-[72px] h-[72px] rounded-[18px] object-cover border-[3px] border-white mb-4"
            />
          ) : (
            <div
              className="w-[72px] h-[72px] rounded-[18px] flex items-center justify-center text-white text-2xl font-bold border-[3px] border-white mb-4"
              style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}DD)` }}
            >
              {initials}
            </div>
          )}

          {/* Name + badges */}
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="font-serif text-[24px] sm:text-[28px] text-navy m-0 leading-tight">
              {company.name}
            </h1>
            {company.verified && <VerifiedBadge size="md" />}
            {company.actively_hiring && (
              <span className="inline-flex items-center gap-1 px-3 py-0.5 rounded-full bg-[#ECFDF5] text-[#047857] text-[12px] font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
                Hiring
              </span>
            )}
          </div>

          {/* Location */}
          {company.location && (
            <p className="text-[15px] text-text-muted mt-1.5 mb-0">{company.location}</p>
          )}

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mt-3.5">
            {sizeLabel && (
              <span className="px-3 py-1 rounded-full bg-warm-bg text-charcoal text-[12px] font-medium border border-border-light">
                {sizeLabel}
              </span>
            )}
            {seasonLabels.map((s) => (
              <span
                key={s}
                className="px-3 py-1 rounded-full bg-gold/8 text-gold text-[12px] font-semibold border border-gold/20"
              >
                {s}
              </span>
            ))}
          </div>

          {/* Response rate */}
          {company.avg_response_hours != null && company.avg_response_hours <= 72 && (
            <div className="mt-3">
              <span className={`inline-flex items-center gap-1.5 text-[13px] font-medium ${
                company.avg_response_hours <= 24
                  ? "text-[#047857]"
                  : "text-[#D97706]"
              }`}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                {company.avg_response_hours <= 24
                  ? "Usually responds within a day"
                  : "Usually responds within a few days"}
              </span>
            </div>
          )}

          {/* Website */}
          {company.website && (
            <a
              href={
                company.website.startsWith("http")
                  ? company.website
                  : `https://${company.website}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-3 text-[13px] text-gold no-underline hover:text-gold-light transition-colors"
            >
              {company.website.replace(/^https?:\/\//, "")}
            </a>
          )}

          {designatedJobsHref && (
            <Link
              href={designatedJobsHref}
              className="mt-5 inline-flex min-h-[44px] w-full items-center justify-center rounded-[10px] bg-navy px-5 py-2 text-[15px] font-semibold text-white no-underline transition-colors hover:bg-navy/90"
            >
              View jobs
            </Link>
          )}

          {/* CTA button */}
          {showCTA && !showMessageForm && (
            <button
              onClick={handleCTAClick}
              className="w-full mt-5 py-3.5 rounded-[10px] bg-gold text-white text-[15px] font-semibold border-none cursor-pointer font-sans transition-all hover:bg-gold-light hover:-translate-y-0.5 shadow-[0_4px_16px_rgba(28, 138, 153,0.25)]"
            >
              Message this employer
            </button>
          )}
          {!showCTA && !userRole && (
            <button
              onClick={handleCTAClick}
              className="w-full mt-5 py-3.5 rounded-[10px] bg-gold text-white text-[15px] font-semibold border-none cursor-pointer font-sans transition-all hover:bg-gold-light hover:-translate-y-0.5 shadow-[0_4px_16px_rgba(28, 138, 153,0.25)]"
            >
              Sign up to message
            </button>
          )}
        </div>
      </div>

      {/* ─── About Section ─── */}
      {company.description && (
        <div className="bg-white rounded-[18px] border border-border-light shadow-sm p-5 sm:p-6 mb-4">
          <div className="text-[11px] font-semibold uppercase tracking-[2.5px] text-gold mb-2">About</div>
          <h2 className="font-serif text-[18px] font-bold text-navy m-0 mb-3">Who we are</h2>
          <p className="text-[14px] leading-[1.7] text-text-body m-0 whitespace-pre-line">
            {company.description}
          </p>
        </div>
      )}

      {openRoles}

      {/* ─── Hiring For ─── */}
      {roleLabels.length > 0 && (
        <div className="bg-white rounded-[18px] border border-border-light shadow-sm p-5 sm:p-6 mb-4">
          <div className="text-[11px] font-semibold uppercase tracking-[2.5px] text-gold mb-2">Roles</div>
          <h2 className="font-serif text-[18px] font-bold text-navy m-0 mb-3.5">Hiring for</h2>
          <div className="flex flex-wrap gap-2">
            {roleLabels.map((label) => (
              <span
                key={label}
                className="px-3.5 py-1.5 rounded-full bg-navy/90 text-white text-[13px] font-medium"
              >
                {label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ─── Locations ─── */}
      {locationLabels.length > 0 && (
        <div className="bg-white rounded-[18px] border border-border-light shadow-sm p-5 sm:p-6 mb-4">
          <div className="flex justify-between items-center mb-3.5">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[2.5px] text-gold mb-2">Where</div>
              <h2 className="font-serif text-[18px] font-bold text-navy m-0">Locations</h2>
            </div>
            {hasMoreLocations && (
              <span className="text-[13px] text-text-muted">
                {locationLabels.length} locations
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {visibleLocations.map((loc) => (
              <span
                key={loc}
                className="px-3 py-1.5 rounded-full bg-warm-bg text-charcoal text-[12px] font-medium border border-border-light"
              >
                {loc}
              </span>
            ))}
          </div>
          {hasMoreLocations && !showAllLocations && (
            <button
              onClick={() => setShowAllLocations(true)}
              className="mt-2.5 bg-transparent border-none text-gold text-[13px] font-semibold cursor-pointer p-0 font-sans hover:text-gold-light transition-colors"
            >
              Show all {locationLabels.length} locations
            </button>
          )}
          {showAllLocations && (
            <button
              onClick={() => setShowAllLocations(false)}
              className="mt-2.5 bg-transparent border-none text-gold text-[13px] font-semibold cursor-pointer p-0 font-sans hover:text-gold-light transition-colors"
            >
              Show fewer
            </button>
          )}
        </div>
      )}

      {/* ─── Perks ─── */}
      {perks.length > 0 && (
        <div className="bg-white rounded-[18px] border border-border-light shadow-sm p-5 sm:p-6 mb-4">
          <div className="text-[11px] font-semibold uppercase tracking-[2.5px] text-gold mb-2">Perks</div>
          <h2 className="font-serif text-[18px] font-bold text-navy m-0 mb-3.5">What&apos;s included</h2>
          <div className="flex flex-col gap-2.5">
            {perks.map((perk) => (
              <div key={perk} className="flex items-center gap-2.5 text-[14px] text-text-body">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="shrink-0">
                  <circle cx="9" cy="9" r="9" fill="#ECFDF5" />
                  <path d="M6 9L8 11L12 7" stroke="#047857" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {perk}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Message Section ─── */}
      <div id="message-section">
        {showCTA ? (
          <div className="bg-white rounded-[18px] border border-border-light shadow-sm p-5 sm:p-6 mb-4">
            {sent ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <p className="text-[15px] font-semibold text-charcoal mb-1">Message sent</p>
                <p className="text-[13px] text-text-muted">
                  {company.name} will see your message in their inbox.
                </p>
              </div>
            ) : showMessageForm ? (
              <div>
                <h2 className="font-serif text-[18px] font-bold text-navy m-0 mb-4">
                  Message {company.name}
                </h2>

                {/* Select availability window */}
                <div className="mb-4">
                  <label className="text-xs font-semibold uppercase tracking-[0.8px] text-charcoal mb-2 block">
                    Which season are you enquiring about?
                  </label>
                  <div className="flex flex-col gap-2">
                    {candidateProfile?.availabilityWindows.map((w) => {
                      const hasConv = conversations.some((c) => c.windowId === w.id);
                      return (
                        <button
                          key={w.id}
                          type="button"
                          disabled={hasConv}
                          onClick={() => setSelectedWindowId(w.id)}
                          className={`px-4 py-3 rounded-[10px] text-left text-sm font-sans border-[1.5px] transition-all cursor-pointer ${
                            hasConv
                              ? "opacity-50 cursor-not-allowed border-border bg-warm-bg text-text-muted"
                              : selectedWindowId === w.id
                              ? "border-gold bg-gold/5 text-charcoal"
                              : "border-border bg-warm-bg text-charcoal hover:border-gold/50"
                          }`}
                        >
                          <span className="font-medium">{w.season_label}</span>
                          {hasConv && (
                            <span className="text-[11px] text-text-muted ml-2">
                              (already messaged)
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Message input */}
                <div className="mb-4">
                  <label className="text-xs font-semibold uppercase tracking-[0.8px] text-charcoal mb-2 block">
                    Your message
                  </label>
                  <textarea
                    value={messageContent}
                    onChange={(e) => setMessageContent(e.target.value)}
                    rows={4}
                    placeholder={`Introduce yourself to ${company.name}...`}
                    className="w-full px-4 py-3 border-[1.5px] border-border rounded-[10px] text-sm font-sans text-charcoal bg-warm-bg resize-y outline-none focus:border-gold focus:bg-white placeholder:text-[#B0A99F] transition-all"
                    maxLength={1000}
                  />
                </div>

                {sendError && (
                  <div className="bg-error/10 border border-error/30 text-error rounded-[10px] px-4 py-2.5 mb-4 text-[13px] font-medium">
                    {sendError}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={handleSendMessage}
                    disabled={sending || !selectedWindowId || !messageContent.trim()}
                    className="px-6 py-3 rounded-sm text-[14px] font-semibold bg-gold text-white border-none cursor-pointer font-sans transition-all hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sending ? "Sending..." : "Send Message"}
                  </button>
                  <button
                    onClick={() => {
                      setShowMessageForm(false);
                      setSendError(null);
                    }}
                    className="px-6 py-3 rounded-sm text-[14px] font-semibold text-text-muted bg-transparent border-[1.5px] border-border cursor-pointer font-sans transition-all hover:border-navy/30"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[14px] text-text-body font-medium mb-1">
                    Interested in working here?
                  </p>
                  <p className="text-[13px] text-text-muted">
                    Send {company.name} a message to express your interest.
                  </p>
                </div>
                <button
                  onClick={() => setShowMessageForm(true)}
                  className="px-6 py-3 rounded-sm text-[14px] font-semibold bg-gold text-white border-none cursor-pointer font-sans transition-all hover:bg-gold-light shrink-0"
                >
                  Message
                </button>
              </div>
            )}
          </div>
        ) : !isSelf && (
          <div className="bg-warm-bg rounded-[18px] px-5 py-5 border border-border-light mb-4">
            <p className="text-[14px] text-text-body font-medium mb-1">
              Want to work here?
            </p>
            <p className="text-[13px] text-text-muted leading-relaxed">
              {company.allow_inbound_messages && userRole !== "candidate"
                ? "Sign up as a candidate to message this company directly."
                : `If ${company.name} is interested in your profile, they'll reach out to you directly through PeakWave messaging. Make sure your profile is complete to increase your chances of being contacted.`}
            </p>
          </div>
        )}
      </div>

      {/* ─── Sticky Mobile CTA ─── */}
      {(showCTA || !userRole) && !showMessageForm && !sent && (
        <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden" style={{ background: "linear-gradient(transparent, #F9F8F4 30%)" }}>
          <div className="px-5 pb-7 pt-3">
            <button
              onClick={handleCTAClick}
              className="w-full max-w-[600px] mx-auto block py-3.5 rounded-[12px] bg-gold text-white text-[15px] font-semibold border-none cursor-pointer font-sans shadow-[0_4px_20px_rgba(28, 138, 153,0.3)] hover:bg-gold-light transition-colors"
            >
              {showCTA ? "Message this employer" : "Sign up to message"}
            </button>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
